import { LogManager } from "@mcleod/core";
import {
    BarcodeScannerDef,
    BarcodeScannerOptions,
    BarcodeScannerTypeHint,
    BarcodeScannerCameraDirection,
    BarcodeScannerScanOrientation,
    BarcodeScanResult
} from "./BarcodeScannerDef";
import { CapacitorAccess } from "../capacitor";
import { MobileDeviceCameraAccessor } from "../camera";

const log = LogManager.getLogger("common.BarcodeScannerAccessor");

/**
 * Provides access to the Capacitor Barcode Scanner plugin.
 * Uses the singleton pattern consistent with other accessors in common-mobile.
 */
export class BarcodeScannerAccessor implements BarcodeScannerDef {
    private static _instance: BarcodeScannerAccessor;

    private constructor() {}

    public static get instance(): BarcodeScannerAccessor {
        this._instance ??= new BarcodeScannerAccessor();
        return this._instance;
    }

    private get plugin(): BarcodeScannerDef | undefined {
        return CapacitorAccess.plugins?.CapacitorBarcodeScanner;
    }

    /**
     * Returns true if the barcode scanner plugin is available.
     */
    public get isAvailable(): boolean {
        return this.plugin !== undefined
            && MobileDeviceCameraAccessor.instance.hasCamera;
    }

    /**
     * Scans a barcode using the device camera.
     * @param options Optional configuration for the scanner.
     * @returns Promise resolving to the scanned barcode string.
     * @throws Error if the scanner plugin is not available or scanning fails.
     */
    public async scanBarcode(options?: BarcodeScannerOptions): Promise<BarcodeScanResult> {
        if (!this.plugin) {
            log.error("BarcodeScanner plugin is not available.");
            throw new Error("Barcode scanner is not available.");
        }

        try {
            // Key rules for iOS compatibility:
            // 1. Every key the Swift Decodable expects MUST be present (non-optional decode).
            // 2. Use " " (space) instead of "" for string defaults — empty strings may be
            //    dropped or nullified during Capacitor bridge serialization.
            // 3. Do NOT include keys with undefined values — Capacitor may convert them to
            //    NSNull, which can cause JSONSerialization/JSONDecoder failures on iOS.
            const nativeOptions: Record<string, any> = {
                hint: options?.hint ?? BarcodeScannerTypeHint.ALL,
                scanInstructions: options?.scanInstructions || " ",
                scanButton: options?.scanButton || false,
                scanText: options?.scanText || " ",
                cameraDirection: options?.cameraDirection || BarcodeScannerCameraDirection.BACK,
                scanOrientation: options?.scanOrientation || BarcodeScannerScanOrientation.ADAPTIVE,
            };
            if (options?.android) {
                nativeOptions.android = options.android;
            }
            if (options?.web) {
                nativeOptions.web = options.web;
            }
            return await this.plugin.scanBarcode(nativeOptions as BarcodeScannerOptions);
        } catch (error) {
            log.error("Failed to scan barcode:", error);
            // Re-throw the original error to preserve Capacitor error properties (code, message)
            // so callers can inspect the native error code for proper handling.
            throw error;
        }
    }
}
